/**
 * Payoff at expiration for option legs / strategies.
 * Returns P/L in USD (includes contract multiplier 100).
 */

function toNum(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

export function collectLegs(position) {
    if (!position) {
        return [];
    }
    const subs = Array.isArray(position.sub_order) ? position.sub_order : [];
    const isGroup = Number(position.counter) > 1 || subs.length > 1;
    if (!isGroup) {
        return [position];
    }

    const legs = [];
    subs.forEach((sub) => {
        const nested = Array.isArray(sub.sub_order) ? sub.sub_order : [];
        if (Number(sub.counter) > 1 || nested.length > 1) {
            nested.forEach((leg) => legs.push(leg));
        } else {
            legs.push(sub);
        }
    });
    return legs.length ? legs : [position];
}

export function formatContractLabel(position) {
    const legs = collectLegs(position);
    if (legs.length === 1) {
        const leg = legs[0];
        return [
            String(leg.symbol || '').toUpperCase(),
            leg.strike != null ? String(leg.strike) : null,
            leg.option_kind || null,
            leg.market || null,
            leg.expire_at || null,
        ].filter(Boolean).join(' ');
    }
    const symbol = String(position.symbol || legs[0]?.symbol || '').toUpperCase();
    const ref = position.reference || position.strategy || 'Strategy';
    return `${symbol} · ${ref} (${legs.length} legs)`;
}

/** Intrinsic value per share at underlying price S. */
function intrinsicPerShare(leg, stockPrice) {
    const strike = toNum(leg.strike);
    const s = toNum(stockPrice);
    const kind = String(leg.option_kind || '').toUpperCase();
    if (kind === 'CALL') {
        return Math.max(s - strike, 0);
    }
    if (kind === 'PUT') {
        return Math.max(strike - s, 0);
    }
    return 0;
}

/** P/L in USD for one leg at expiration underlying price. */
export function legPayoffUsd(leg, stockPrice) {
    const qty = Math.max(1, toNum(leg.quantity, 1));
    const premium = Math.abs(toNum(leg.open_price));
    const intrinsic = intrinsicPerShare(leg, stockPrice);
    const market = String(leg.market || '').toUpperCase();
    const perShare = market === 'SELL'
        ? premium - intrinsic
        : intrinsic - premium;
    return perShare * 100 * qty;
}

export function strategyPayoffUsd(position, stockPrice) {
    return collectLegs(position).reduce(
        (sum, leg) => sum + legPayoffUsd(leg, stockPrice),
        0,
    );
}

export function resolvePriceWindow(position, currentStockPrice) {
    const legs = collectLegs(position);
    const strikes = legs.map((leg) => toNum(leg.strike)).filter((n) => n > 0);
    const midStrike = strikes.length
        ? (Math.min(...strikes) + Math.max(...strikes)) / 2
        : toNum(currentStockPrice, 100);
    const spot = toNum(currentStockPrice, midStrike);
    const span = Math.max(
        midStrike * 0.25,
        (Math.max(...strikes, spot) - Math.min(...strikes, spot)) * 1.5,
        5,
    );
    let min = Math.max(0.01, Math.min(spot, ...strikes, midStrike) - span);
    let max = Math.max(spot, ...strikes, midStrike) + span;

    // Keep a readable step count (~80 points)
    const roughStep = (max - min) / 80;
    const step = roughStep >= 1
        ? Math.max(0.5, Math.round(roughStep * 2) / 2)
        : roughStep >= 0.25
            ? 0.25
            : 0.1;

    min = Math.floor(min / step) * step;
    max = Math.ceil(max / step) * step;
    return { min, max, step, spot };
}

export function buildPayoffSeries(position, currentStockPrice) {
    const { min, max, step, spot } = resolvePriceWindow(position, currentStockPrice);
    const labels = [];
    const values = [];

    for (let price = min; price <= max + step / 2; price += step) {
        const p = Number(price.toFixed(4));
        labels.push(p);
        values.push(Number(strategyPayoffUsd(position, p).toFixed(2)));
    }

    const breakevens = [];
    for (let i = 1; i < values.length; i += 1) {
        const a = values[i - 1];
        const b = values[i];
        if ((a <= 0 && b >= 0) || (a >= 0 && b <= 0)) {
            if (b === a) {
                breakevens.push(labels[i]);
            } else {
                const t = -a / (b - a);
                const be = labels[i - 1] + t * (labels[i] - labels[i - 1]);
                breakevens.push(Number(be.toFixed(2)));
            }
        }
    }

    const currentPl = Number(strategyPayoffUsd(position, spot).toFixed(2));
    const maxProfit = Math.max(...values);
    const maxLoss = Math.min(...values);
    const strikes = [...new Set(
        collectLegs(position)
            .map((leg) => toNum(leg.strike))
            .filter((n) => n > 0),
    )].sort((a, b) => a - b);

    return {
        labels,
        values,
        spot,
        currentPl,
        maxProfit,
        maxLoss,
        breakevens: [...new Set(breakevens)].slice(0, 4),
        strikes,
    };
}
